import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  useTheme,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { CircuitVm } from 'api/picking/circuit.vm';
import { GetCircuitsVm } from 'api/picking/get-circuits.vm';
import { getCircuits } from 'api/picking/get-circuits';
import { assignTeamToCircuit } from 'api/picking/assign-team-to-circuit';
import { unassignTeamFromCircuit } from 'api/picking/unassign-team-from-circuit';
import { assignPreparationAreaToCircuit } from 'api/picking/assign-preparation-area-to-circuit';
import { useSnackbar } from 'hooks/use-snackbar';
import TeamSelectionDialog from 'components/TeamSelectionDialog';
import PreparationAreaSelectionDialog from 'components/PreparationAreaSelectionDialog';

const CircuitPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { circuitId } = useParams<{ circuitId: string }>();
  const [circuit, setCircuit] = useState<CircuitVm | null>(null);
  const [loading, setLoading] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [assigningTeam, setAssigningTeam] = useState(false);
  const [preparationAreaDialogOpen, setPreparationAreaDialogOpen] = useState(false);
  const [assigningPreparationArea, setAssigningPreparationArea] = useState(false);
  const snackbar = useSnackbar();

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchCircuit = () => {
    if (!circuitId) return;

    setLoading(true);
    // Since we don't have a getCircuit endpoint, we'll fetch all and find the one we need
    getCircuits('', null, {
      success: (response: GetCircuitsVm) => {
        const foundCircuit = response.objects.find((c) => c.id === circuitId);
        setCircuit(foundCircuit || null);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter circuito:', err);
        snackbar.show('Falha ao carregar dados do circuito', null, 'error');
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchCircuit();
  }, [circuitId]);

  const handleUnassignTeam = () => {
    if (!circuitId) return;

    unassignTeamFromCircuit(circuitId, {
      success: () => {
        snackbar.show('Equipa desatribuída com sucesso!', null, 'success');
        fetchCircuit();
      },
      error: (err) => {
        console.error('Falha ao desatribuir equipa:', err);
        snackbar.show('Falha ao desatribuir equipa', null, 'error');
      },
    });
  };

  const handleAssignTeam = (teamId: string) => {
    if (!circuitId) return;

    setAssigningTeam(true);
    assignTeamToCircuit(circuitId, teamId, {
      success: () => {
        snackbar.show('Equipa atribuída com sucesso!', null, 'success');
        setTeamDialogOpen(false);
        setAssigningTeam(false);
        fetchCircuit();
      },
      error: (err) => {
        console.error('Falha ao atribuir equipa:', err);
        snackbar.show('Falha ao atribuir equipa', null, 'error');
        setAssigningTeam(false);
      },
    });
  };

  const handleAssignPreparationArea = (preparationAreaId: string) => {
    if (!circuitId) return;

    setAssigningPreparationArea(true);
    assignPreparationAreaToCircuit(circuitId, preparationAreaId, {
      success: () => {
        snackbar.show('Área de preparação atribuída com sucesso!', null, 'success');
        setPreparationAreaDialogOpen(false);
        setAssigningPreparationArea(false);
        fetchCircuit();
      },
      error: (err) => {
        console.error('Falha ao atribuir área de preparação:', err);
        snackbar.show('Falha ao atribuir área de preparação', null, 'error');
        setAssigningPreparationArea(false);
      },
    });
  };

  const getPriorityChip = (priority: number) => {
    if (priority <= 3) {
      return <Chip label={`Prioridade ${priority}`} color="error" size="small" />;
    } else if (priority <= 6) {
      return <Chip label={`Prioridade ${priority}`} color="warning" size="small" />;
    } else {
      return <Chip label={`Prioridade ${priority}`} color="success" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" onClick={handleBackNavigation} sx={{ cursor: 'pointer' }}>
              Circuitos
            </Link>
            <Typography color="text.primary">Carregando...</Typography>
          </Breadcrumbs>
        </Box>
        <CircularProgress />
      </Box>
    );
  }

  if (!circuit) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Circuito não encontrado
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" onClick={handleBackNavigation} sx={{ cursor: 'pointer' }}>
              Circuitos
            </Link>
            <Typography color="text.primary">{circuit.name}</Typography>
          </Breadcrumbs>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Informações do Circuito
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mr: 2, minWidth: 120 }}>
                  Nome:
                </Typography>
                <Typography variant="body1">{circuit.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mr: 2, minWidth: 120 }}>
                  Armazém:
                </Typography>
                <Typography variant="body1">{circuit.warehouseId}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mr: 2, minWidth: 120 }}>
                  Prioridade:
                </Typography>
                {getPriorityChip(circuit.priority)}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">Equipa:</Typography>
                {circuit.teamName ? (
                  <Typography variant="body1">{circuit.teamName}</Typography>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Sem equipa
                  </Typography>
                )}
              </Box>
              <Box>
                {circuit.teamName ? (
                  <Button variant="outlined" color="error" size="small" onClick={handleUnassignTeam}>
                    Desatribuir
                  </Button>
                ) : (
                  <Button variant="contained" size="small" onClick={() => setTeamDialogOpen(true)}>
                    Atribuir Equipa
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">Área de Preparação:</Typography>
                {circuit.preparationAreaId ? (
                  <Typography variant="body1">{circuit.preparationAreaId}</Typography>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Sem área
                  </Typography>
                )}
              </Box>
              <Box>
                {!circuit.preparationAreaId && (
                  <Button variant="contained" size="small" onClick={() => setPreparationAreaDialogOpen(true)}>
                    Atribuir Área
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <TeamSelectionDialog
        open={teamDialogOpen}
        onClose={() => setTeamDialogOpen(false)}
        onSubmit={handleAssignTeam}
        loading={assigningTeam}
        warehouseId={circuit.warehouseId}
      />

      <PreparationAreaSelectionDialog
        open={preparationAreaDialogOpen}
        onClose={() => setPreparationAreaDialogOpen(false)}
        onSubmit={handleAssignPreparationArea}
        loading={assigningPreparationArea}
        warehouseId={circuit.warehouseId}
      />
    </>
  );
};

export default CircuitPage;
